import { ShareChatTrigger, type ShareChatHandlers } from "./share-chat-trigger";
import type { ShareLinkData } from "./share-chat-dialog";

interface ShareApiResponse {
  data?: ShareLinkData | null;
}
interface Unwrappable<T> {
  unwrap: () => Promise<T>;
}

interface Props {
  sessionId: string;
  conversationTitle?: string;
  variant?: "icon" | "button";
  /** Restyle the trigger (e.g. to match a toolbar's ghost icons). */
  triggerClassName?: string;
  isFetching: boolean;
  isCreating: boolean;
  isRevoking: boolean;
  fetchShareLink: (arg: { sessionId: string }) => Unwrappable<ShareApiResponse>;
  createShareLink: (arg: {
    sessionId: string;
    expiresInDays?: number;
  }) => Unwrappable<ShareApiResponse>;
  revokeShareLink: (arg: { sessionId: string }) => Unwrappable<unknown>;
}

/**
 * Scope-agnostic share trigger: my-space and company each call their own
 * RTK Query hooks (separate api slices, so Rules of Hooks rules out picking
 * one dynamically) and hand the trigger functions here — this is where the
 * identical handlers-building/rendering logic used to be duplicated.
 */
export const AiShareTrigger = ({
  sessionId,
  conversationTitle,
  variant,
  triggerClassName,
  isFetching,
  isCreating,
  isRevoking,
  fetchShareLink,
  createShareLink,
  revokeShareLink,
}: Props) => {
  const handlers: ShareChatHandlers = {
    isFetching,
    isCreating,
    isRevoking,
    fetchShare: async (sid) => {
      const res = await fetchShareLink({ sessionId: sid }).unwrap();
      return res?.data ?? null;
    },
    createShare: async (sid, expiresInDays) => {
      const res = await createShareLink({
        sessionId: sid,
        ...(expiresInDays != null ? { expiresInDays } : {}),
      }).unwrap();
      return res?.data ?? null;
    },
    revokeShare: async (sid) => {
      await revokeShareLink({ sessionId: sid }).unwrap();
    },
  };

  return (
    <ShareChatTrigger
      sessionId={sessionId}
      conversationTitle={conversationTitle}
      handlers={handlers}
      variant={variant}
      triggerClassName={triggerClassName}
    />
  );
};
